import { FC, createContext, useContext, useState, useCallback } from 'react';

type BannersContextOpenType =
  | 'proToPlus'
  | 'freePremiun'
  | 'freePremiunFor30Days';
type BannersContextOpens = {
  [currentType in BannersContextOpenType]: boolean;
};

interface BannersContextValue {
  opens: BannersContextOpens;
  getIsOpenByType: (currentType: BannersContextOpenType) => boolean;
  handleOpenByType: (currentType: BannersContextOpenType) => void;
  handleCloseByType: (currentType: BannersContextOpenType) => void;
}

const DEFAULTS_BANNERS_OPENS: BannersContextOpens = {
  proToPlus: false,
  freePremiun: false,
  freePremiunFor30Days: false,
};

const BannersContext = createContext<BannersContextValue>({
  opens: DEFAULTS_BANNERS_OPENS,
  getIsOpenByType: () => false,
  handleOpenByType: () => {},
  handleCloseByType: () => {},
});

export const useBannersContext = (): BannersContextValue =>
  useContext(BannersContext);

const BannersProvider: FC = ({ children }) => {
  const [opens, setOpens] = useState<BannersContextOpens>(
    DEFAULTS_BANNERS_OPENS,
  );

  const getIsOpenByType = useCallback(
    (currentType: BannersContextOpenType) => opens[currentType],
    [opens],
  );

  const handleOpenByType = useCallback(
    (currentType: BannersContextOpenType) => {
      setOpens((prevOpens) => ({
        ...prevOpens,
        [currentType]: true,
      }));
    },
    [],
  );

  const handleCloseByType = useCallback(
    (currentType: BannersContextOpenType) => {
      setOpens((prevOpens) => ({
        ...prevOpens,
        [currentType]: false,
      }));
    },
    [],
  );

  return (
    <BannersContext.Provider
      value={{ opens, getIsOpenByType, handleOpenByType, handleCloseByType }}
    >
      {children}
    </BannersContext.Provider>
  );
};

export default BannersProvider;
